/**
 * @file AvitoFinancePage.tsx
 * @description Кабинет «Финансы Avito» (phase14.0.4).
 *
 * 3 таба:
 *  - Баланс — профиль + 3 тайла (real / bonus / CPA-вычисляемый)
 *  - Операции — таблица + фильтры
 *  - Настройки — retention/archive
 *
 * Кнопка «Получить с Avito» (общая для всех табов) открывает FetchFromAvitoDialog
 * с чекбоксами что именно дёрнуть.
 */

import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { ApiError } from "../../../lib/api-client";
import {
  shopAvitoFinance,
  type AvitoFinanceSnapshotDto,
} from "../../../lib/api-shop-avito-finance";
import { AvitoFinanceBalanceTab } from "./finance/AvitoFinanceBalanceTab";
import { AvitoFinanceOperationsTab } from "./finance/AvitoFinanceOperationsTab";
import { AvitoFinanceSettingsTab } from "./finance/AvitoFinanceSettingsTab";
import { FetchFromAvitoDialog } from "./finance/FetchFromAvitoDialog";

type TabId = "balance" | "operations" | "settings";

export function AvitoFinancePage() {
  const [tab, setTab] = useState<TabId>("balance");
  const [snapshot, setSnapshot] = useState<AvitoFinanceSnapshotDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [fetchDialogOpen, setFetchDialogOpen] = useState(false);
  const [opsFilter, setOpsFilter] = useState<string | undefined>();

  const reloadSnapshot = useCallback(async () => {
    setError(null);
    try {
      setSnapshot(await shopAvitoFinance.snapshot());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось загрузить snapshot.");
    }
  }, []);

  useEffect(() => {
    void reloadSnapshot();
  }, [reloadSnapshot]);

  function openCpaOperations(): void {
    setOpsFilter("cpa");
    setTab("operations");
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <nav className="flex gap-1 border-b border-outline-variant -mb-px">
          <TabButton active={tab === "balance"} onClick={() => setTab("balance")}>Баланс</TabButton>
          <TabButton active={tab === "operations"} onClick={() => setTab("operations")}>Операции</TabButton>
          <TabButton active={tab === "settings"} onClick={() => setTab("settings")}>Настройки</TabButton>
        </nav>
        <Button onClick={() => setFetchDialogOpen(true)}>
          <RefreshCw className="size-4" />
          Получить с Avito
        </Button>
      </div>

      {error && <Alert severity="error">{error}</Alert>}

      {tab === "balance" && (
        <AvitoFinanceBalanceTab snapshot={snapshot} onOpenCpaOperations={openCpaOperations} />
      )}
      {tab === "operations" && <AvitoFinanceOperationsTab initialServiceType={opsFilter} />}
      {tab === "settings" && <AvitoFinanceSettingsTab />}

      <FetchFromAvitoDialog
        open={fetchDialogOpen}
        onClose={() => setFetchDialogOpen(false)}
        onFetched={() => {
          void reloadSnapshot();
        }}
      />
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
        active
          ? "text-primary border-primary"
          : "text-on-surface-variant border-transparent hover:text-on-surface",
      ].join(" ")}
    >
      {children}
    </button>
  );
}
